import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import MenuIcon from '@mui/icons-material/Menu';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ManageSidebar, { DRAWER_WIDTH } from '../../components/ManageSidebar';
import MatchContext from '../../contexts/MatchContext';
import { useAuth } from '../../auth/AuthContext';
import { getMatch as fetchMatchApi, getMatchAuth } from '../../api';
import ManageLogin from './ManageLogin';
import AppHeader, { isAppMode } from '../../components/AppHeader';
import PullToRefresh from '../../components/PullToRefresh';

export default function ManageWrapper() {
  const { matchId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [match, setMatch] = useState(null);
  const [auth, setAuth] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [authAttempt, setAuthAttempt] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Fetch match data
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchMatchApi(matchId);
        setMatch(res.data);
      } catch {
        setError('Failed to load match');
      }
    })();
  }, [matchId]);

  // Fetch auth/permissions
  const fetchAuth = useCallback(async () => {
    const hasToken = !!user || !!sessionStorage.getItem('accessToken');
    if (!hasToken) {
      setNeedsLogin(true);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setNeedsLogin(false);
      const res = await getMatchAuth(matchId);
      const authData = res.data;

      if (!authData || (!authData.permissions?.length && !authData.admin && !authData.owner)) {
        setError('You do not have permission to manage this match');
        setLoading(false);
        return;
      }

      setPermissions(authData);
      setAuth(authData);

      // Score-only staff: redirect to scoring page if on index
      const isAdmin = authData.role === 'owner' || authData.permissions?.includes('manage');
      if (!isAdmin && authData.permissions?.includes('score')) {
        const basePath = `/${matchId}/manage`;
        const currentPath = location.pathname.replace(/\/$/, '');
        if (currentPath === basePath) {
          navigate(`${basePath}/scores`, { replace: true });
        }
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setNeedsLogin(true);
      } else {
        setError('Failed to verify permissions');
      }
    } finally {
      setLoading(false);
    }
  }, [matchId, user, authAttempt]);

  useEffect(() => {
    fetchAuth();
  }, [fetchAuth]);

  const handleAuth = useCallback(() => {
    setAuthAttempt((a) => a + 1);
  }, []);

  if (needsLogin) {
    return (
      <>
        <AppHeader />
        <ManageLogin matchId={matchId} match={match} onAuth={handleAuth} />
      </>
    );
  }

  if (loading || !match) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const contextValue = {
    match,
    setMatch,
    auth,
    setAuth,
    permissions,
    refreshMatch: async () => {
      const res = await fetchMatchApi(matchId);
      setMatch(res.data);
    },
  };

  const outletContext = {
    ...contextValue,
    permissions,
  };

  return (
    <MatchContext.Provider value={contextValue}>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <ManageSidebar
          permissions={permissions}
          mobileOpen={mobileOpen}
          onClose={() => setMobileOpen(false)}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: isMobile ? '100%' : `calc(100% - ${DRAWER_WIDTH}px)`,
            display: 'flex',
            flexDirection: 'column',
            '@media print': { width: '100%' },
          }}
        >
          {/* Mobile top bar with hamburger */}
          {isMobile && (
            <AppBar position="sticky" className="noprint" sx={{ backgroundColor: 'rgba(0,0,0,0.85)' }}>
              <Toolbar variant="dense">
                <IconButton
                  color="inherit"
                  edge="start"
                  onClick={() => setMobileOpen(true)}
                  sx={{ mr: 2 }}
                >
                  <MenuIcon />
                </IconButton>
                <Typography variant="subtitle1" noWrap>
                  {match.title}
                </Typography>
              </Toolbar>
            </AppBar>
          )}

          {isAppMode() ? (
            <PullToRefresh onRefresh={() => window.location.reload()}>
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Outlet context={outletContext} />
              </Box>
            </PullToRefresh>
          ) : (
            <Box sx={{ p: { xs: 2, sm: 3 }, flex: 1 }}>
              <Outlet context={outletContext} />
            </Box>
          )}
        </Box>
      </Box>
    </MatchContext.Provider>
  );
}
